import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { useSection } from '../../hooks/useSection';

interface CategoryFilterProps {
  selectedCategory: string;
  selectedType: string;
  onCategoryChange: (category: string) => void;
  onTypeChange: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  items: any[];
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  selectedCategory,
  selectedType,
  onCategoryChange,
  onTypeChange,
  config,
  translations,
  items
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  
  // Obtenir les catégories disponibles
  const categories = React.useMemo(() => {
    const uniqueCategories = Array.from(new Set(
      items.map(item => item.category)
    )).filter(Boolean);
    
    return uniqueCategories.sort((a, b) => {
      const aText = translations[a]?.[i18n.language] || translations[a]?.fr || a;
      const bText = translations[b]?.[i18n.language] || translations[b]?.fr || b;
      return aText.localeCompare(bText);
    });
  }, [items, translations, i18n.language]);

  // Obtenir les types disponibles pour la catégorie sélectionnée
  const types = React.useMemo(() => {
    if (!selectedCategory) return [];
    
    const uniqueTypes = Array.from(new Set(
      items
        .filter(item => item.category === selectedCategory)
        .map(item => item.type)
    )).filter(Boolean);
    
    return uniqueTypes.sort((a, b) => {
      const aText = translations[a]?.[i18n.language] || translations[a]?.fr || a;
      const bText = translations[b]?.[i18n.language] || translations[b]?.fr || b;
      return aText.localeCompare(bText);
    });
  }, [items, selectedCategory, translations, i18n.language]);

  // Réinitialiser le type si la catégorie change
  useEffect(() => {
    if (selectedType) {
      const itemWithType = items.find(item => item.type === selectedType);
      if (!itemWithType || itemWithType.category !== selectedCategory) {
        onTypeChange('');
      }
    }
  }, [selectedCategory, selectedType, items, onTypeChange]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'category_filter',
      event_data: {
        section_id: section.id,
        section_name: section.names?.fr || 'Unknown',
        section_type: section.type_rubrique || 'Unknown',
        filter_value: category || 'all'
      }
    });
    onCategoryChange(category);
    onTypeChange(''); // Réinitialiser le type quand la catégorie change
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const type = e.target.value;
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'type_filter',
      event_data: {
        section_id: section.id,
        section_name: section.names?.fr || 'Unknown',
        section_type: section.type_rubrique || 'Unknown',
        filter_value: type || 'all',
        category: selectedCategory
      }
    });
    onTypeChange(type);
  };

  return (
    <div 
      className="flex border-t border-white/30"
      style={{ backgroundColor: config['major-color'] }}
    >
      {/* Category Filter */}
      <div 
        className={`px-4 py-3 flex items-center justify-between relative ${
          selectedCategory && types.length > 0 ? 'w-1/2' : 'w-full'
        }`}
        style={{ 
          borderRight: selectedCategory && types.length > 0 
            ? '1px solid rgba(255, 255, 255, 0.2)' 
            : 'none'
        }}
      >
        <select
          value={selectedCategory}
          onChange={handleCategoryChange}
          className="bg-transparent text-white appearance-none cursor-pointer pr-8 focus:outline-none w-full truncate"
          style={{ color: config['light-color'] }}
        >
          <option value="">{getTranslatedText('filter_all_categories')}</option>
          {categories.map(category => (
            <option key={category} value={category}>
              {getTranslatedCategory(category)}
            </option>
          ))}
        </select>
        <ChevronDown 
          size={20} 
          className="absolute right-4 text-white pointer-events-none"
          style={{ color: config['light-color'] }}
        />
      </div>

      {/* Type Filter - Only show if category is selected and types exist */}
      {selectedCategory && types.length > 0 && (
        <div 
          className="w-1/2 px-4 py-3 flex items-center justify-between relative"
        >
          <select
            value={selectedType}
            onChange={handleTypeChange}
            className="bg-transparent text-white appearance-none cursor-pointer pr-8 focus:outline-none w-full truncate"
            style={{ color: config['light-color'] }}
          >
            <option value="">{getTranslatedText('filter_all_types')}</option>
            {types.map(type => (
              <option key={type} value={type}>
                {getTranslatedCategory(type)}
              </option>
            ))}
          </select>
          <ChevronDown 
            size={20} 
            className="absolute right-4 text-white pointer-events-none"
            style={{ color: config['light-color'] }}
          />
        </div>
      )}
    </div>
  );
};